import { useCallback, useEffect, useMemo, useState } from "react";

import debounce from "lodash.debounce";
import { useNavigate } from "react-router";

import { Button } from "@components/Button";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { useDialogStore } from "@storage/global/useDialogStore";
import { useVerificationInfoQuery } from "@storage/queries/ageVerification";
import { useStartBroadcastMutation, useStopBroadcastMutation } from "@storage/queries/broadcast";
import { useStartAwayModeMutation, useStopAwayModeMutation } from "@storage/queries/broadcast/away";
import { useBroadcastStatusDataQuery } from "@storage/queries/broadcast/useBroadcastStatusDataQuery";
import { useStartSpyModeMutation, useStopSpyModeMutation } from "@storage/queries/outerPrivate";

import { DIALOG_KEYS } from "@services/DialogManager";

import { cn } from "@lib/utils";

import { DELAY_TIME_1000 } from "@configs/constants";
import { ROUTES } from "@configs/routes";

const STATUS_LABEL = {
	offline: () => "Offline",
	public: (isOnline: boolean) => (isOnline ? "Online" : "Offline"),
	private: () => "Online",
	away: (isOnline: boolean) => (isOnline ? "Online" : "Offline"),
	default: () => "Offline",
} as const;

export const BroadcastControlBar = () => {
	const [broadcastValue, setBroadcastValue] = useState("Offline");
	const [isSpyMode, setIsSpyMode] = useState(false);
	const [isAwayModeState, setIsAwayModeState] = useState(false);
	const [isBroadcastRequestScheduled, setIsBroadcastRequestScheduled] = useState(false);

	const startBroadcastMutation = useStartBroadcastMutation();
	const stopBroadcastMutation = useStopBroadcastMutation();
	const startAwayModeMutation = useStartAwayModeMutation();
	const stopAwayModeMutation = useStopAwayModeMutation();
	const startSpyModeMutation = useStartSpyModeMutation();
	const stopSpyModeMutation = useStopSpyModeMutation();
	const startBroadcast = startBroadcastMutation.mutate;
	const stopBroadcast = stopBroadcastMutation.mutate;

	const { openDialog } = useDialogStore();
	const verificationInfoQuery = useVerificationInfoQuery();
	const { broadcastStatus, isOnline, isAwayMode, privateInfo } = useBroadcastStatusDataQuery();
	const { MOBILE, TABLET } = useLayoutBreakpoint();
	const isMobile = MOBILE || TABLET;
	const navigate = useNavigate();

	const requiresVerification = verificationInfoQuery.isSuccess && verificationInfoQuery.data.verification_required;
	const isBroadcastPending =
		isBroadcastRequestScheduled || startBroadcastMutation.isPending || stopBroadcastMutation.isPending;
	const isSpyPending = startSpyModeMutation.isPending || stopSpyModeMutation.isPending;
	const isAwayPending =
		startAwayModeMutation.isPending || stopAwayModeMutation.isPending || stopBroadcastMutation.isPending;
	const isStatusChangePending = isBroadcastPending || isSpyPending || isAwayPending;

	useEffect(() => {
		const status = STATUS_LABEL[broadcastStatus ?? "default"](isOnline);
		setBroadcastValue(status);
		setIsAwayModeState(isAwayMode);
		setIsSpyMode(privateInfo?.outerPrivate ?? false);
	}, [broadcastStatus, isOnline, isAwayMode, privateInfo?.outerPrivate]);

	const handleOpenVerificationRequiredDialog = useCallback(() => {
		openDialog(DIALOG_KEYS.VERIFICATION_REQUIRED, {
			onNavigate: () => {
				navigate(ROUTES.AGE_VERIFICATION);
			},
		});
	}, [navigate, openDialog]);

	const handleBroadcastRequestSettled = useCallback(() => {
		setIsBroadcastRequestScheduled(false);
	}, []);

	const runDebouncedBroadcastRequest = useMemo(
		() =>
			debounce((prevState: string) => {
				if (prevState === "Offline") {
					startBroadcast(undefined, {
						onSuccess: () => setBroadcastValue("Online"),
						onSettled: handleBroadcastRequestSettled,
					});
				} else {
					stopBroadcast(undefined, {
						onSuccess: () => setBroadcastValue("Offline"),
						onSettled: handleBroadcastRequestSettled,
					});
				}
			}, DELAY_TIME_1000),
		[handleBroadcastRequestSettled, startBroadcast, stopBroadcast],
	);

	useEffect(
		() => () => {
			runDebouncedBroadcastRequest.cancel();
		},
		[runDebouncedBroadcastRequest],
	);

	const handleBroadcastValue = useCallback(() => {
		if (requiresVerification && broadcastValue === "Offline") {
			handleOpenVerificationRequiredDialog();
			return;
		}
		setIsBroadcastRequestScheduled(true);
		runDebouncedBroadcastRequest(broadcastValue);
	}, [broadcastValue, handleOpenVerificationRequiredDialog, requiresVerification, runDebouncedBroadcastRequest]);

	const handleSpyValue = useCallback(() => {
		if (requiresVerification) {
			handleOpenVerificationRequiredDialog();
			return;
		}
		const next = !isSpyMode;
		if (next) {
			startSpyModeMutation.mutate(undefined, { onSuccess: () => setIsSpyMode(next) });
		} else {
			stopSpyModeMutation.mutate(undefined, { onSuccess: () => setIsSpyMode(next) });
		}
	}, [
		handleOpenVerificationRequiredDialog,
		isSpyMode,
		requiresVerification,
		startSpyModeMutation,
		stopSpyModeMutation,
	]);

	const handleAwayValue = useCallback(() => {
		if (requiresVerification) {
			handleOpenVerificationRequiredDialog();
			return;
		}
		const next = !isAwayModeState;
		if (next) {
			startAwayModeMutation.mutate(undefined, { onSuccess: () => setIsAwayModeState(next) });
		} else {
			if (!isOnline) {
				stopBroadcastMutation.mutate(undefined, { onSuccess: () => setIsAwayModeState(next) });
			} else {
				stopAwayModeMutation.mutate(undefined, { onSuccess: () => setIsAwayModeState(next) });
			}
		}
	}, [
		handleOpenVerificationRequiredDialog,
		isAwayModeState,
		isOnline,
		requiresVerification,
		startAwayModeMutation,
		stopAwayModeMutation,
		stopBroadcastMutation,
	]);

	return (
		<Stack spacing={8} direction="row" wide>
			<div
				className={cn("relative flex rounded-lg border border-secondary p-1", isMobile ? "w-[50%]" : "w-full")}
				onClick={isStatusChangePending ? undefined : handleBroadcastValue}
			>
				<Button
					className="rounded-sm px-2 py-1.75"
					type={broadcastValue === "Offline" ? "secondary" : "link"}
					size="small"
					wide={isMobile}
					disabled={isStatusChangePending}
				>
					<Typography type="span" padding="none">
						Offline
					</Typography>
				</Button>
				<Button
					className="rounded-sm px-2 py-1.75"
					type={broadcastValue === "Online" ? "success" : "link"}
					size="small"
					wide={isMobile}
					disabled={isStatusChangePending}
				>
					<Typography type="span" padding="none">
						Online
					</Typography>
				</Button>
			</div>

			<Stack direction="row" spacing={8} className={isMobile ? "w-[50%]" : "w-full"}>
				<div className={cn("rounded-lg border border-secondary p-1", isMobile ? "w-full" : "")}>
					<Button
						className="rounded-sm px-2 py-1.75"
						type={isSpyMode ? "spy" : "link"}
						onClick={handleSpyValue}
						size="small"
						wide
						disabled={isStatusChangePending}
					>
						<Typography type="span" padding="none">
							Spy
						</Typography>
					</Button>
				</div>
				<div className={cn("rounded-lg border border-secondary p-1", isMobile ? "w-full" : "")}>
					<Button
						className="rounded-sm px-2 py-1.75"
						type={isAwayModeState ? "red200" : "link"}
						onClick={handleAwayValue}
						size="small"
						wide
						disabled={isStatusChangePending}
					>
						<Typography type="span" padding="none">
							Away
						</Typography>
					</Button>
				</div>
			</Stack>
		</Stack>
	);
};
